import {
  type AttributionRow,
  type SpeakerIdentity,
  type TranscriptSource,
} from '@/pages/app/AttributionViewer/artifactDerivations'

// One hue family per slot (blue, green, pink, amber, cyan, orange, violet, red) so neighbours never read alike.
export const CATEGORICAL = ['#2a78d6', '#008300', '#e87ba4', '#eda100', '#00a3bf', '#eb6834', '#4a3aa7', '#e34948']
// Unassigned speakers read as greyscale; provider clusters follow named speakers, then live clusters.
export const UNIDENTIFIED = ['#4f4f4b', '#777670', '#9b9a93', '#bdbcb4', '#d2d1ca']

// Fixed so a capture channel keeps its color no matter how many speakers the other lanes contribute.
// Mid-tone on purpose: the hatch bands these light and dark, and both bands have to stay readable.
const SOURCES: Record<`source:${TranscriptSource}`, string> = {
  'source:mic': '#c2185b',
  'source:refined': '#6b3fb0',
  'source:system': '#0f7d8c',
}

export const isSourceKey = (colorKey: string): colorKey is `source:${TranscriptSource}` =>
  colorKey.startsWith('source:')
export const isClusterKey = (colorKey: string): boolean => colorKey.startsWith('cluster:')
const isLiveClusterKey = (colorKey: string): boolean => colorKey.startsWith('cluster:live:')

export const NO_VOTE_COLOR = '#898781'
export const NO_VOTE_LABEL = 'No speaker identity'

export type SpeakerStyle = {
  color: string
  colorKey: string
  label: string
  pattern: 'solid' | 'diagonal' | 'dots' | 'hatch' | 'channels'
  backgroundImage?: string
}

export type SpeakerStyleMap = Map<string, SpeakerStyle>

export const LIVE_OVERLAP_STYLE: SpeakerStyle = {
  color: SOURCES['source:mic'],
  colorKey: 'live-overlap',
  label: 'Mic + System',
  pattern: 'channels',
  backgroundImage: `repeating-linear-gradient(135deg, ${SOURCES['source:mic']} 0 3px, ${SOURCES['source:system']} 3px 6px)`,
}

export const SILENCE_STYLE: SpeakerStyle = {
  color: NO_VOTE_COLOR,
  colorKey: 'silence',
  label: 'Silence',
  pattern: 'hatch',
}

export function styleForAttribution(
  row: Pick<AttributionRow, 'speaker' | 'observationState'>,
  styles: SpeakerStyleMap
): SpeakerStyle {
  return row.observationState === 'silence' ? SILENCE_STYLE : styleForSpeaker(row.speaker, styles)
}

const LABEL_PREFIXES = [
  /^(?:Live|Soniox|Chorus) · /,
  /^(?:Refined|Mapping|Original|GradientBoost|Classical|Rescue|Assigner) · \S+ · /,
  /^AX tree · participant · /,
  /^AX tree · participant /,
]

export const shortLabel = (style: Pick<SpeakerStyle, 'label'>): string => {
  for (const prefix of LABEL_PREFIXES) {
    if (prefix.test(style.label)) return style.label.replace(prefix, '')
  }
  return style.label
}

const PATTERNS: SpeakerStyle['pattern'][] = ['solid', 'diagonal', 'dots']

export function buildSpeakerStyleMap(identities: Iterable<SpeakerIdentity>): SpeakerStyleMap {
  const unique = new Map<string, SpeakerIdentity>()
  for (const identity of Array.from(identities)) unique.set(identity.key, identity)
  const ordered = Array.from(unique.values()).sort(
    (a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key, undefined, { numeric: true })
  )
  const colorKeys = Array.from(new Set(ordered.map((identity) => identity.colorKey))).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  )
  const namedColorKeys = new Set(
    ordered.filter((identity) => identity.name?.trim()).map((identity) => identity.colorKey)
  )
  const sourceKeys = colorKeys.filter(isSourceKey)
  const speakerKeys = [
    ...colorKeys.filter(
      (colorKey) => !isSourceKey(colorKey) && !isLiveClusterKey(colorKey) && namedColorKeys.has(colorKey)
    ),
    ...colorKeys.filter(
      (colorKey) => isClusterKey(colorKey) && !isLiveClusterKey(colorKey) && !namedColorKeys.has(colorKey)
    ),
    ...colorKeys.filter(isLiveClusterKey),
  ]
  const unidentifiedKeys = colorKeys.filter(
    (colorKey) => !isSourceKey(colorKey) && !isClusterKey(colorKey) && !namedColorKeys.has(colorKey)
  )
  const styleByColorKey = new Map([
    ...sourceKeys.map((colorKey) => [colorKey, { color: SOURCES[colorKey], pattern: 'hatch' as const }] as const),
    ...speakerKeys.map(
      (colorKey, index) =>
        [
          colorKey,
          {
            color: CATEGORICAL[index % CATEGORICAL.length],
            pattern: PATTERNS[Math.floor(index / CATEGORICAL.length) % PATTERNS.length],
          },
        ] as const
    ),
    ...unidentifiedKeys.map(
      (colorKey, index) =>
        [
          colorKey,
          {
            color: UNIDENTIFIED[index % UNIDENTIFIED.length],
            pattern: PATTERNS[Math.floor(index / UNIDENTIFIED.length) % PATTERNS.length],
          },
        ] as const
    ),
  ])
  return new Map(
    ordered.map((identity) => [
      identity.key,
      {
        ...styleByColorKey.get(identity.colorKey)!,
        colorKey: identity.colorKey,
        label: identity.label,
      },
    ])
  )
}

// Extends a finished map: existing keys never move and new colorKeys continue each palette where the base stopped.
export function extendSpeakerStyleMap(base: SpeakerStyleMap, identities: Iterable<SpeakerIdentity>): SpeakerStyleMap {
  const unique = new Map<string, SpeakerIdentity>()
  for (const identity of Array.from(identities)) if (!base.has(identity.key)) unique.set(identity.key, identity)
  if (unique.size === 0) return base

  const baseByColorKey = new Map<string, SpeakerStyle>()
  for (const style of Array.from(base.values()))
    if (!baseByColorKey.has(style.colorKey)) baseByColorKey.set(style.colorKey, style)
  const usedSlots = (palette: string[]) =>
    Array.from(baseByColorKey.values()).filter((style) => palette.includes(style.color)).length

  const ordered = Array.from(unique.values()).sort(
    (a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key, undefined, { numeric: true })
  )
  const namedColorKeys = new Set(
    ordered.filter((identity) => identity.name?.trim()).map((identity) => identity.colorKey)
  )
  const newColorKeys = Array.from(new Set(ordered.map((identity) => identity.colorKey)))
    .filter((colorKey) => !baseByColorKey.has(colorKey))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  const named = [
    ...newColorKeys.filter((colorKey) => namedColorKeys.has(colorKey) && !isLiveClusterKey(colorKey)),
    ...newColorKeys.filter(isLiveClusterKey),
  ]
  const unidentified = newColorKeys.filter((colorKey) => !namedColorKeys.has(colorKey) && !isLiveClusterKey(colorKey))
  const namedStart = usedSlots(CATEGORICAL)
  const unidentifiedStart = usedSlots(UNIDENTIFIED)

  const slotByColorKey = new Map<string, Pick<SpeakerStyle, 'color' | 'pattern'>>()
  for (const [colorKey, style] of Array.from(baseByColorKey)) slotByColorKey.set(colorKey, style)
  const assign = (colorKeys: string[], palette: string[], start: number) =>
    colorKeys.forEach((colorKey, offset) => {
      const index = start + offset
      slotByColorKey.set(colorKey, {
        color: palette[index % palette.length],
        pattern: PATTERNS[Math.floor(index / palette.length) % PATTERNS.length],
      })
    })
  assign(named, CATEGORICAL, namedStart)
  assign(unidentified, UNIDENTIFIED, unidentifiedStart)

  const next = new Map(base)
  for (const identity of ordered) {
    const { color, pattern } = slotByColorKey.get(identity.colorKey)!
    next.set(identity.key, { color, pattern, colorKey: identity.colorKey, label: identity.label })
  }
  return next
}

export function styleForSpeaker(identity: SpeakerIdentity | null, styles: SpeakerStyleMap): SpeakerStyle {
  if (!identity) return { color: NO_VOTE_COLOR, colorKey: 'unknown', label: NO_VOTE_LABEL, pattern: 'diagonal' }
  return (
    styles.get(identity.key) ?? {
      color: NO_VOTE_COLOR,
      colorKey: identity.colorKey,
      label: identity.label,
      pattern: 'diagonal',
    }
  )
}
